package com.banyan.flightviewer.web.state

import com.banyan.flightviewer.platform.SettingsStore
import com.banyan.flightviewer.state.AircraftData
import com.banyan.flightviewer.state.AppState
import com.banyan.flightviewer.web.components.aircraft.AircraftFilters
import com.banyan.flightviewer.web.components.aircraft.SortColumn
import com.banyan.flightviewer.web.components.aircraft.SortDirection
import com.banyan.flightviewer.web.components.aircraft.filterAircraft
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn

/**
 * Web-only UI state: search query, filter state, toggles for the filter /
 * bookmarks panels, and the aircraft-table sort column/direction.
 *
 * Lives on [com.banyan.flightviewer.web.state.WebAppContainer] so every
 * component gets the same instance. Values persisted through
 * [SettingsStore] (localStorage) survive page reloads; in-flight search text
 * intentionally does not.
 *
 * Exposes a single derived [filteredAircraftList] that the map, the table,
 * and any future consumer subscribe to. Keeps the filter logic in exactly
 * one place.
 */
class WebUiState(appState: AppState, private val store: SettingsStore) {

    // ── Raw state ────────────────────────────────────────────────────────
    private val _searchQuery = MutableStateFlow("")
    val searchQuery: StateFlow<String> = _searchQuery.asStateFlow()

    private val _filters = MutableStateFlow(loadFilters())
    val filters: StateFlow<AircraftFilters> = _filters.asStateFlow()

    private val _filtersVisible = MutableStateFlow(false)
    val filtersVisible: StateFlow<Boolean> = _filtersVisible.asStateFlow()

    private val _bookmarksVisible = MutableStateFlow(false)
    val bookmarksVisible: StateFlow<Boolean> = _bookmarksVisible.asStateFlow()

    private val _sortColumn = MutableStateFlow(loadSortColumn())
    val sortColumn: StateFlow<SortColumn> = _sortColumn.asStateFlow()

    private val _sortDirection = MutableStateFlow(loadSortDirection())
    val sortDirection: StateFlow<SortDirection> = _sortDirection.asStateFlow()

    /**
     * Current map viewport expressed as a query center + radius. The map
     * pushes updates here on every `moveend` / `zoomend`; the monitoring
     * loop reads this to query aircraft in the visible area instead of the
     * user's home location. Null until the map is ready; callers fall back
     * to `settings.lat/lon/radiusKm` in that case.
     *
     * Mirrors desktop's `MapManager.getMapQueryArea()` result.
     */
    private val _mapQueryArea = MutableStateFlow<MapQueryArea?>(null)
    val mapQueryArea: StateFlow<MapQueryArea?> = _mapQueryArea.asStateFlow()

    fun setMapQueryArea(area: MapQueryArea?) {
        _mapQueryArea.value = area
    }

    /**
     * One-shot "please pan the map to this spot" request. MapView binds
     * to this, calls `map.setView(...)`, then clears the flow so the
     * next request can fire even if it's the same coords.
     *
     * Drivers: the deep-link share flow (jump to a flight's position by
     * hex), future "jump to favorited airport" features. Null = no
     * pending request.
     */
    private val _mapFocusRequest = MutableStateFlow<MapFocus?>(null)
    val mapFocusRequest: StateFlow<MapFocus?> = _mapFocusRequest.asStateFlow()

    fun requestMapFocus(lat: Double, lon: Double, zoom: Int? = null) {
        _mapFocusRequest.value = MapFocus(lat, lon, zoom)
    }

    fun clearMapFocus() {
        _mapFocusRequest.value = null
    }

    // ── Derived flow: single source of truth for "what to show" ──────────
    val filteredAircraftList: StateFlow<List<AircraftData>> =
        combine(appState.aircraftList, _searchQuery, _filters) { list, q, f ->
            filterAircraft(list, q, f)
        }.stateIn(appState.scope, SharingStarted.Eagerly, emptyList())

    // ── Mutators ─────────────────────────────────────────────────────────
    fun setSearchQuery(value: String) {
        _searchQuery.value = value
    }

    fun setFilters(value: AircraftFilters) {
        _filters.value = value
        saveFilters(value)
    }

    fun resetFilters() = setFilters(AircraftFilters())

    fun toggleFiltersVisible() {
        _filtersVisible.value = !_filtersVisible.value
    }

    fun setFiltersVisible(value: Boolean) {
        _filtersVisible.value = value
    }

    fun toggleBookmarksVisible() {
        _bookmarksVisible.value = !_bookmarksVisible.value
    }

    fun setBookmarksVisible(value: Boolean) {
        _bookmarksVisible.value = value
    }

    /**
     * Click a column header: if it's the same column, flip direction; else
     * pick that column fresh in ASC direction. Matches desktop behaviour.
     */
    fun toggleSort(column: SortColumn) {
        if (_sortColumn.value == column) {
            val next = if (_sortDirection.value == SortDirection.ASC) SortDirection.DESC else SortDirection.ASC
            _sortDirection.value = next
            store.putString(KEY_SORT_DIR, next.name)
        } else {
            _sortColumn.value = column
            _sortDirection.value = SortDirection.ASC
            store.putString(KEY_SORT_COL, column.name)
            store.putString(KEY_SORT_DIR, SortDirection.ASC.name)
        }
        store.flush()
    }

    // ── Persistence helpers ──────────────────────────────────────────────
    private fun loadFilters(): AircraftFilters = AircraftFilters(
        altMin = store.getInt(KEY_ALT_MIN) ?: 0,
        altMax = store.getInt(KEY_ALT_MAX) ?: 60_000,
        showGround = store.getBoolean(KEY_SHOW_GROUND) ?: true,
        showAirborne = store.getBoolean(KEY_SHOW_AIRBORNE) ?: true,
        airlinePrefix = store.getString(KEY_AIRLINE_PREFIX) ?: "",
    )

    private fun saveFilters(f: AircraftFilters) {
        store.putInt(KEY_ALT_MIN, f.altMin)
        store.putInt(KEY_ALT_MAX, f.altMax)
        store.putBoolean(KEY_SHOW_GROUND, f.showGround)
        store.putBoolean(KEY_SHOW_AIRBORNE, f.showAirborne)
        store.putString(KEY_AIRLINE_PREFIX, f.airlinePrefix)
        store.flush()
    }

    private fun loadSortColumn(): SortColumn {
        val raw = store.getString(KEY_SORT_COL) ?: return SortColumn.DISTANCE
        return runCatching { SortColumn.valueOf(raw) }.getOrDefault(SortColumn.DISTANCE)
    }

    private fun loadSortDirection(): SortDirection {
        val raw = store.getString(KEY_SORT_DIR) ?: return SortDirection.ASC
        return runCatching { SortDirection.valueOf(raw) }.getOrDefault(SortDirection.ASC)
    }

    private companion object {
        const val KEY_SORT_COL = "ui_sort_col"
        const val KEY_SORT_DIR = "ui_sort_dir"
        const val KEY_ALT_MIN = "ui_filter_alt_min"
        const val KEY_ALT_MAX = "ui_filter_alt_max"
        const val KEY_SHOW_GROUND = "ui_filter_ground"
        const val KEY_SHOW_AIRBORNE = "ui_filter_airborne"
        const val KEY_AIRLINE_PREFIX = "ui_filter_airline"
    }
}

/**
 * Query parameters derived from the map's visible area. Center is the
 * viewport center; [radiusKm] is the distance from center to the farthest
 * visible corner (plus a small buffer, capped so we don't ask upstream
 * for the whole hemisphere).
 */
data class MapQueryArea(val lat: Double, val lon: Double, val radiusKm: Double)

/** One-shot request to pan the map to (lat, lon) with optional zoom. */
data class MapFocus(val lat: Double, val lon: Double, val zoom: Int? = null)
